import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  Firestore 
} from 'firebase/firestore';
import { getFunctions, httpsCallable, Functions } from 'firebase/functions';
import { useAuth } from '../../hooks/AuthProvider';
import UserIcon from '../authentication/UserIcon';
import AdminDashboard from './AdminDashboard';
import ConferenceManager from './ConferenceManager';
import ConferenceAccess from './ConferenceAccess';
import app from '../../Firebase';
import '../../styles/AdminPanel.css';

import { ClientFirebaseConfig } from '../client/clientStorageService';

// Add this interface near the top of your AdminPanel
interface AdminPanelProps {
  db: Firestore;
  clientStorageConfig?: ClientFirebaseConfig; // Add client storage config
}


/**
 * Admin Panel - Requires APP_ADMIN permission
 * 
 * Permissions allowed in this component:
 * - APP_ADMIN: Can view all content and access all features
 * 
 * Future permissions to consider:
 * - CONFERENCE_ADMIN: Could be limited to only managing conferences
 * - USER_ADMIN: Could be limited to only managing users
 * - CONTENT_ADMIN: Could be limited to only managing content
 */

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  db, 
  clientStorageConfig 
}) => 
{
  const { currentUser } = useAuth();
  const [activeSection, setActiveSection] = useState<string | null>("conferences");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const functions = getFunctions(app, 'europe-west2');
  // const functions = getFunctions(app);
  
  // Clear success and error messages when changing sections
  useEffect(() => {
    setError('');
    setSuccess('');
  }, [activeSection]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
  };

  // Helper to automatically dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="header-title">
          <h1>Admin Panel</h1>
        </div>
        <div className="header-user">
          <UserIcon />
        </div>
      </header>

      {error && (
        <div className="error-alert">
          <button className="close-alert" onClick={() => setError('')}>×</button>
          {error}
        </div>
      )}

      {success && (
        <div className="success-alert">
          <button className="close-alert" onClick={() => setSuccess('')}>×</button>
          {success}
        </div>
      )}

      <div className="admin-content">
        <div className="admin-sidebar">
          <h2>Administration</h2>
          <nav className="admin-nav">
            <button 
              className={`admin-nav-item ${activeSection === 'conferences' ? 'active' : ''}`}
              onClick={() => handleSectionChange('conferences')}
            >
              Manage Conferences
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'access' ? 'active' : ''}`}
              onClick={() => handleSectionChange('access')}
            >
              Manage Access
            </button>
            <button 
              className={`admin-nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleSectionChange('dashboard')}
            >
              Admin Dashboard
            </button>
          </nav>
        </div>

        <div className="admin-main-content">
          {!activeSection && (
            <div className="empty-section">
              <h2>Welcome to Admin Panel</h2>
              <p>Select a section from the sidebar to get started.</p>
            </div>
          )}

          {activeSection === 'dashboard' && (
            <AdminDashboard
              db={db}
              functions={functions}
              onError={setError}
              onSuccess={setSuccess}
              setLoading={setLoading}
            />
          )}

          {activeSection === 'conferences' && (
            <ConferenceManager 
              db={db} 
              functions={functions}
              onError={setError}
              onSuccess={setSuccess}
              setLoading={setLoading}
              clientStorageConfig={clientStorageConfig}
            />
          )}

          {activeSection === 'access' && (
            <ConferenceAccess
              db={db}
              functions={functions}
              onError={setError}
              onSuccess={setSuccess}
              setLoading={setLoading}
            />
          )}

          {activeSection === 'users' && (
            <div className="section-placeholder">
              <h2>User Management</h2>
              <p>This section is not yet implemented.</p>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;